import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  Upload,
  Download,
  Trash2,
  HelpCircle,
  BarChart3,
  CheckCircle,
  XCircle,
  Users,
  FileSpreadsheet,
  Loader2,
  RefreshCw,
  Plus,
  Edit,
  Save,
  X,
  User,
  Calendar,
  Target
} from 'lucide-react';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DailyQuestionManager() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [userResponses, setUserResponses] = useState([]);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [activeTab, setActiveTab] = useState('questions');
  
  // Add/Edit Question State
  const [showQuestionForm, setShowQuestionForm] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [questionForm, setQuestionForm] = useState({
    question: '',
    option_1: '',
    option_2: '',
    correct_answer: 1,
    explanation: ''
  });

  const fetchSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/settings/daily-question`);
      setEnabled(response.data.enabled || false);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    }
  }, []);

  const fetchQuestions = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/daily-questions`);
      setQuestions(response.data.questions || []);
      setTotalQuestions(response.data.total || 0);
    } catch (error) {
      console.error('Failed to fetch questions:', error);
    }
  }, []);

  const fetchAnalytics = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/daily-questions/analytics`);
      setAnalytics(response.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    }
  }, []);

  const fetchUserResponses = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/daily-questions/user-responses`);
      setUserResponses(response.data.responses || []);
    } catch (error) {
      console.error('Failed to fetch user responses:', error);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchSettings(), fetchQuestions(), fetchAnalytics(), fetchUserResponses()]);
      setLoading(false);
    };
    loadData();
  }, [fetchSettings, fetchQuestions, fetchAnalytics, fetchUserResponses]);

  const handleToggleEnabled = async () => {
    try {
      await axios.post(`${API_URL}/settings/daily-question?enabled=${!enabled}`);
      setEnabled(!enabled);
      toast.success(`Daily Question feature ${!enabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      toast.error('Failed to update settings');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Please upload an Excel file (.xlsx or .xls)');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API_URL}/admin/daily-questions/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.questions_added > 0) {
        toast.success(`Successfully uploaded ${response.data.questions_added} questions`);
        fetchQuestions();
        fetchAnalytics();
      }

      if (response.data.errors?.length > 0) {
        toast.warning(`Some rows had errors: ${response.data.errors.join(', ')}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload questions');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/daily-questions/template`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'daily_questions_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const handleDeleteQuestion = async (questionId) => {
    if (!window.confirm('Are you sure you want to delete this question?')) return;

    try {
      await axios.delete(`${API_URL}/admin/daily-questions/${questionId}`);
      toast.success('Question deleted');
      fetchQuestions();
      fetchAnalytics();
    } catch (error) {
      toast.error('Failed to delete question');
    }
  };

  const resetQuestionForm = () => {
    setQuestionForm({
      question: '',
      option_1: '',
      option_2: '',
      correct_answer: 1,
      explanation: ''
    });
    setEditingQuestion(null);
    setShowQuestionForm(false);
  };

  const handleAddQuestion = () => {
    resetQuestionForm();
    setShowQuestionForm(true);
  };

  const handleEditQuestion = (q) => {
    setEditingQuestion(q);
    setQuestionForm({
      question: q.question,
      option_1: q.option_1,
      option_2: q.option_2,
      correct_answer: q.correct_answer,
      explanation: q.explanation || ''
    });
    setShowQuestionForm(true);
  };

  const handleSaveQuestion = async () => {
    if (!questionForm.question || !questionForm.option_1 || !questionForm.option_2) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSaving(true);
    try {
      if (editingQuestion) {
        // Update existing question
        await axios.put(`${API_URL}/admin/daily-questions/${editingQuestion.id}`, questionForm);
        toast.success('Question updated successfully');
      } else {
        // Create new question
        await axios.post(`${API_URL}/admin/daily-questions`, questionForm);
        toast.success('Question added successfully');
      }
      resetQuestionForm();
      fetchQuestions();
      fetchAnalytics();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save question');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 p-4 md:p-6" data-testid="daily-question-manager">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">Question of the Day</h1>
            <p className="text-muted-foreground">
              Manage daily questions that appear to users on login
            </p>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Label htmlFor="feature-toggle" className="text-sm">
                {enabled ? 'Enabled' : 'Disabled'}
              </Label>
              <Switch
                id="feature-toggle"
                checked={enabled}
                onCheckedChange={handleToggleEnabled}
                data-testid="daily-question-toggle"
              />
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b overflow-x-auto">
          <button
            onClick={() => setActiveTab('questions')}
            className={`px-4 py-2 font-medium transition-colors whitespace-nowrap ${
              activeTab === 'questions'
                ? 'border-b-2 border-primary text-primary'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <HelpCircle className="h-4 w-4 inline mr-2" />
            Questions ({totalQuestions})
          </button>
          <button
            onClick={() => setActiveTab('analytics')}
            className={`px-4 py-2 font-medium transition-colors whitespace-nowrap ${
              activeTab === 'analytics'
                ? 'border-b-2 border-primary text-primary'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <BarChart3 className="h-4 w-4 inline mr-2" />
            Analytics
          </button>
          <button
            onClick={() => { setActiveTab('users'); fetchUserResponses(); }}
            className={`px-4 py-2 font-medium transition-colors whitespace-nowrap ${
              activeTab === 'users'
                ? 'border-b-2 border-primary text-primary'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Users className="h-4 w-4 inline mr-2" />
            User Responses
          </button>
        </div>

        {/* Questions Tab */}
        {activeTab === 'questions' && (
          <div className="space-y-4">
            {/* Add/Edit Question Form */}
            {showQuestionForm && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    {editingQuestion ? <Edit className="h-5 w-5" /> : <Plus className="h-5 w-5" />}
                    {editingQuestion ? 'Edit Question' : 'Add New Question'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="question">Question *</Label>
                    <Textarea
                      id="question"
                      value={questionForm.question}
                      onChange={(e) => setQuestionForm({...questionForm, question: e.target.value})}
                      placeholder="Enter your question here..."
                      rows={2}
                    />
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="option_1">Option 1 *</Label>
                      <Input
                        id="option_1"
                        value={questionForm.option_1}
                        onChange={(e) => setQuestionForm({...questionForm, option_1: e.target.value})}
                        placeholder="First option"
                      />
                    </div>
                    <div>
                      <Label htmlFor="option_2">Option 2 *</Label>
                      <Input
                        id="option_2"
                        value={questionForm.option_2}
                        onChange={(e) => setQuestionForm({...questionForm, option_2: e.target.value})}
                        placeholder="Second option"
                      />
                    </div>
                  </div>

                  <div>
                    <Label>Correct Answer *</Label>
                    <div className="flex gap-4 mt-2">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="correct_answer"
                          checked={questionForm.correct_answer === 1}
                          onChange={() => setQuestionForm({...questionForm, correct_answer: 1})}
                          className="w-4 h-4"
                        />
                        <span>Option 1</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="correct_answer"
                          checked={questionForm.correct_answer === 2}
                          onChange={() => setQuestionForm({...questionForm, correct_answer: 2})}
                          className="w-4 h-4"
                        />
                        <span>Option 2</span>
                      </label>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="explanation">Explanation</Label>
                    <Textarea
                      id="explanation"
                      value={questionForm.explanation}
                      onChange={(e) => setQuestionForm({...questionForm, explanation: e.target.value})}
                      placeholder="Explain why this answer is correct..."
                      rows={3}
                    />
                  </div>

                  <div className="flex gap-2 justify-end">
                    <Button variant="outline" onClick={resetQuestionForm}>
                      <X className="h-4 w-4 mr-2" />
                      Cancel
                    </Button>
                    <Button onClick={handleSaveQuestion} disabled={saving}>
                      {saving ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Save className="h-4 w-4 mr-2" />
                      )}
                      {editingQuestion ? 'Update Question' : 'Save Question'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Upload Section */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Manage Questions</CardTitle>
                <CardDescription>
                  Add questions manually or upload an Excel file
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-3">
                  <Button onClick={handleAddQuestion} data-testid="add-question-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Question
                  </Button>
                  
                  <Button
                    variant="outline"
                    onClick={handleDownloadTemplate}
                    data-testid="download-template-btn"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download Template
                  </Button>
                  
                  <div className="relative">
                    <Input
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={handleFileUpload}
                      disabled={uploading}
                      className="hidden"
                      id="question-upload"
                      data-testid="question-upload-input"
                    />
                    <Label htmlFor="question-upload" className="cursor-pointer">
                      <Button variant="outline" disabled={uploading} asChild>
                        <span>
                          {uploading ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            <Upload className="h-4 w-4 mr-2" />
                          )}
                          {uploading ? 'Uploading...' : 'Upload Excel'}
                        </span>
                      </Button>
                    </Label>
                  </div>

                  <Button
                    variant="ghost"
                    onClick={() => {
                      fetchQuestions();
                      fetchAnalytics();
                    }}
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Refresh
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Questions List */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <FileSpreadsheet className="h-5 w-5" />
                  Uploaded Questions
                </CardTitle>
              </CardHeader>
              <CardContent>
                {questions.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <HelpCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No questions uploaded yet.</p>
                    <p className="text-sm">Add questions manually or upload an Excel file.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {questions.map((q, index) => (
                      <div
                        key={q.id}
                        className="border rounded-lg p-4 hover:bg-muted/50 transition-colors"
                        data-testid={`question-item-${index}`}
                      >
                        <div className="flex justify-between items-start gap-4">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm md:text-base">{q.question}</p>
                            <div className="mt-2 flex flex-wrap gap-2 text-sm text-muted-foreground">
                              <Badge variant="outline">
                                1: {q.option_1}
                              </Badge>
                              <Badge variant="outline">
                                2: {q.option_2}
                              </Badge>
                              <Badge variant="default" className="bg-green-600">
                                Answer: {q.correct_answer}
                              </Badge>
                            </div>
                            {q.explanation && (
                              <p className="mt-2 text-sm text-muted-foreground line-clamp-2">
                                {q.explanation}
                              </p>
                            )}
                          </div>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleEditQuestion(q)}
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              data-testid={`edit-question-${index}`}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteQuestion(q.id)}
                              className="text-destructive hover:text-destructive hover:bg-destructive/10"
                              data-testid={`delete-question-${index}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && analytics && (
          <div className="space-y-4">
            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-2">
                    <HelpCircle className="h-5 w-5 text-blue-500" />
                    <div>
                      <p className="text-2xl font-bold">{analytics.total_questions}</p>
                      <p className="text-sm text-muted-foreground">Total Questions</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-2">
                    <Users className="h-5 w-5 text-purple-500" />
                    <div>
                      <p className="text-2xl font-bold">{analytics.unique_users_participated}</p>
                      <p className="text-sm text-muted-foreground">Users Participated</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-green-500" />
                    <div>
                      <p className="text-2xl font-bold">{analytics.correct_responses}</p>
                      <p className="text-sm text-muted-foreground">Correct Answers</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-2">
                    <XCircle className="h-5 w-5 text-red-500" />
                    <div>
                      <p className="text-2xl font-bold">{analytics.wrong_responses}</p>
                      <p className="text-sm text-muted-foreground">Wrong Answers</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Overall Stats */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Overall Performance</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span>Total Responses</span>
                    <span className="font-bold">{analytics.total_responses}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span>Today's Responses</span>
                    <span className="font-bold">{analytics.today_responses}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span>Overall Accuracy</span>
                    <Badge variant={analytics.overall_accuracy >= 70 ? 'default' : 'destructive'}>
                      {analytics.overall_accuracy}%
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Per-Question Stats */}
            {analytics.question_stats?.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Question-wise Performance</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {analytics.question_stats.map((stat, index) => (
                      <div
                        key={stat.question_id}
                        className="border rounded-lg p-3"
                      >
                        <p className="font-medium text-sm">{stat.question}</p>
                        <div className="mt-2 flex flex-wrap gap-2 text-sm">
                          <Badge variant="outline">
                            {stat.total_responses} responses
                          </Badge>
                          <Badge variant="outline" className="bg-green-100 text-green-700">
                            {stat.correct_responses} correct
                          </Badge>
                          <Badge variant={stat.accuracy_rate >= 70 ? 'default' : 'destructive'}>
                            {stat.accuracy_rate}% accuracy
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* User Responses Tab */}
        {activeTab === 'users' && (
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  User Response History
                </CardTitle>
                <CardDescription>
                  See which users answered which questions
                </CardDescription>
              </CardHeader>
              <CardContent>
                {userResponses.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No responses yet.</p>
                    <p className="text-sm">User responses will appear here once they start answering.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-3 px-2">User</th>
                          <th className="text-left py-3 px-2">Question</th>
                          <th className="text-center py-3 px-2">Answer</th>
                          <th className="text-center py-3 px-2">Result</th>
                          <th className="text-left py-3 px-2">Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {userResponses.map((response, index) => (
                          <tr key={response.id || index} className="border-b hover:bg-muted/50">
                            <td className="py-3 px-2">
                              <div className="flex items-center gap-2">
                                <User className="h-4 w-4 text-muted-foreground" />
                                <div>
                                  <p className="font-medium">{response.user_name || 'Unknown'}</p>
                                  <p className="text-xs text-muted-foreground">{response.user_email}</p>
                                </div>
                              </div>
                            </td>
                            <td className="py-3 px-2 max-w-xs">
                              <p className="truncate" title={response.question_text}>
                                {response.question_text || 'Question deleted'}
                              </p>
                            </td>
                            <td className="py-3 px-2 text-center">
                              <Badge variant="outline">
                                Option {response.selected_answer}
                              </Badge>
                            </td>
                            <td className="py-3 px-2 text-center">
                              {response.is_correct ? (
                                <Badge className="bg-green-100 text-green-700">
                                  <CheckCircle className="h-3 w-3 mr-1" />
                                  Correct
                                </Badge>
                              ) : (
                                <Badge className="bg-red-100 text-red-700">
                                  <XCircle className="h-3 w-3 mr-1" />
                                  Wrong
                                </Badge>
                              )}
                            </td>
                            <td className="py-3 px-2">
                              <div className="flex items-center gap-1 text-muted-foreground">
                                <Calendar className="h-3 w-3" />
                                <span className="text-xs">
                                  {new Date(response.answered_at).toLocaleDateString()}
                                </span>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
